/**
 * FlowPage — Orchestrator for the flow editor page.
 *
 * This component has a single responsibility:
 * composing hooks and sub-components without containing business logic.
 *
 * Feature structure:
 *   constants.ts          — constants (API, edge styles, markers)
 *   utils.ts              — pure data transformation functions
 *   hooks/useFlowLoader   — fetches/creates the flow and hydrates the canvas
 *   hooks/useFlowSaver    — persists the flow to the API
 *   hooks/useNodeManager  — CRUD for nodes and edges
 *   hooks/useContextMenu  — context menu state and auto-close
 *   hooks/useKeyboardShortcuts — keyboard shortcuts
 *   hooks/useUndoRedo     — undo/redo history
 *   components/FlowCanvas         — ReactFlow canvas + visual plugins
 *   components/EditorContextMenu  — context menu UI
 *   hooks/useConnectOnDrop    — detects drop on empty canvas and manages popup
 *   components/ConnectDropMenu — node creation popup via drag-and-drop
 */
import { useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useNodesState, useEdgesState } from '@xyflow/react'

// Shared reusable components
import TriggerNode   from '../../components/chatflow/TriggerNode'
import StepNode      from '../../components/chatflow/StepNode'
import NodeSidebar   from '../../components/chatflow/NodeSidebar'
import FlowToolbar   from '../../components/chatflow/FlowToolbar'
import ConfirmDialog from '../../components/chatflow/ConfirmDialog'
import useFlowStore  from '../../components/chatflow/store'
import {
  FlowGlobalStyles,
  EditorRoot,
  EditorBody,
  LoadingScreen,
  LoadingSpinner,
  Toast,
} from '../../components/chatflow/styles'

// Page-specific sub-components
import FlowCanvas        from './components/FlowCanvas'
import EditorContextMenu from './components/EditorContextMenu'
import EditorStatusBar   from './components/EditorStatusBar'
import ConnectDropMenu   from './components/ConnectDropMenu'

// Page-specific hooks
import { useUndoRedo }          from './hooks/useUndoRedo'
import { useFlowLoader }        from './hooks/useFlowLoader'
import { useFlowSaver }         from './hooks/useFlowSaver'
import { useNodeManager }       from './hooks/useNodeManager'
import { useContextMenu }       from './hooks/useContextMenu'
import { useKeyboardShortcuts } from './hooks/useKeyboardShortcuts'
import { useConnectOnDrop }     from './hooks/useConnectOnDrop'

/* ── nodeTypes defined outside the component (stable reference — @xyflow/react requirement) ── */
const nodeTypes = { trigger: TriggerNode, step: StepNode }

/* ════════════════════════════════════════════════════════════
   FlowPage
════════════════════════════════════════════════════════════ */
export default function FlowPage() {
  const { t } = useTranslation()
  /* ── Store: rendering selectors ── */
  const loading       = useFlowStore((s) => s.loading)
  const selectedNode  = useFlowStore((s) => s.selectedNode)
  const toast         = useFlowStore((s) => s.toast)
  const confirmDialog = useFlowStore((s) => s.confirmDialog)
  const setDirty      = useFlowStore((s) => s.setDirty)
  const setContextMenu  = useFlowStore((s) => s.setContextMenu)
  const setSelectedNode = useFlowStore((s) => s.setSelectedNode)
  const hideConfirm     = useFlowStore((s) => s.hideConfirm)

  /* ── React Flow state (must live in the parent component — @xyflow/react requirement) ── */
  const [nodes, setNodes, onNodesChange] = useNodesState([])
  const [edges, setEdges, onEdgesChange] = useEdgesState([])

  /* ── Refs ── */
  const rfInstanceRef  = useRef(null)
  const canvasRef      = useRef(null)
  const initialLoadRef = useRef(true) // coordinates dirty tracking during initial load

  /* ── Feature hooks ── */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { pushState, undo, redo, canUndo, canRedo } = useUndoRedo(nodes, edges, setNodes as (nodes: any[]) => void, setEdges as (edges: any[]) => void)

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  useFlowLoader({ setNodes: setNodes as (nodes: any[]) => void, setEdges: setEdges as (edges: any[]) => void, initialLoadRef })

  const { handleSave } = useFlowSaver({ nodes })

  const {
    onConnect,
    onNodeClick,
    onPaneClick,
    handleNodeUpdate,
    handleDeleteNode,
    addNode,
    addConnectedNode,
    duplicateNode,
    onNodesDelete,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } = useNodeManager({ nodes, setNodes: setNodes as (updater: any) => void, setEdges: setEdges as (updater: any) => void, rfInstanceRef })

  const { onNodeContextMenu, onPaneContextMenu } = useContextMenu()

  useKeyboardShortcuts({ handleSave, undo, redo, selectedNode, duplicateNode, setContextMenu })

  /* ── Connect-on-drop: detects handle drag released on empty canvas ── */
  const { onConnectEnd, dropState, dismiss } = useConnectOnDrop({ rfInstanceRef })

  /* Callback invoked by ConnectDropMenu when a node type is selected */
  const handleDropCreate = (type: string) => {
    if (!dropState) return
    addConnectedNode(
      type,
      { x: dropState.flowX, y: dropState.flowY },
      dropState.fromNodeId,
      dropState.fromHandle,
    )
    dismiss()
  }

  /* ── Dirty tracking: records changes after initial load ── */
  useEffect(() => {
    if (initialLoadRef.current) return
    setDirty(true)
    pushState()
  }, [nodes, edges])

  /* ── Stats computed for the status bar ── */
  const stats = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const real  = nodes.filter((n: any) => n.type !== 'trigger')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const warns = real.filter((n: any) => !n.data.messages || !n.data.messages[0]?.trim()).length
    return { nodes: real.length, edges: edges.length, warns }
  }, [nodes, edges])

  /* ── Loading state ── */
  if (loading) {
    return (
      <>
        <FlowGlobalStyles />
        <LoadingScreen>
          <LoadingSpinner />
          <span>{t('flow.loading')}</span>
        </LoadingScreen>
      </>
    )
  }

  /* ── Render ── */
  return (
    <>
      <FlowGlobalStyles />
      <EditorRoot>

        <FlowToolbar
          onSave={handleSave}
          onUndo={undo}
          onRedo={redo}
          canUndo={canUndo}
          canRedo={canRedo}
        />

        <EditorBody>
          <FlowCanvas
            canvasRef={canvasRef}
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onConnectEnd={onConnectEnd}
            onNodeClick={onNodeClick}
            onPaneClick={onPaneClick}
            onNodeContextMenu={onNodeContextMenu}
            onPaneContextMenu={onPaneContextMenu}
            onInit={(instance) => { rfInstanceRef.current = instance }}
            onNodesDelete={onNodesDelete}
            nodeTypes={nodeTypes}
            addNode={addNode}
          />

          {selectedNode && (
            <NodeSidebar
              node={selectedNode}
              nodes={nodes}
              edges={edges}
              onUpdate={handleNodeUpdate}
              onDelete={handleDeleteNode}
              onClose={() => setSelectedNode(null)}
            />
          )}
        </EditorBody>

        <EditorStatusBar stats={stats} />

        <EditorContextMenu
          onAddNode={addNode}
          onDuplicate={duplicateNode}
          onDelete={handleDeleteNode}
        />

        {/* Node creation popup via handle drag-and-drop */}
        {dropState && (
          <ConnectDropMenu
            screenX={dropState.screenX}
            screenY={dropState.screenY}
            onSelect={handleDropCreate}
            onDismiss={dismiss}
          />
        )}

        {toast && (
          <Toast $type={toast.type}>{toast.message}</Toast>
        )}

        {confirmDialog && (
          <ConfirmDialog
            message={confirmDialog.message}
            onConfirm={() => { confirmDialog.onConfirm?.(); hideConfirm() }}
            onCancel={() => { confirmDialog.onCancel?.(); hideConfirm() }}
          />
        )}

      </EditorRoot>
    </>
  )
}
